import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CountUpDirective } from '../../directives/count-up.directive';
import { SubValueItem } from '../../models/types'; // Import the new interface

@Component({
  selector: 'app-metrics-card',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, MatTooltipModule, CountUpDirective],
  template: `
    <mat-card class="metric-card" appearance="outlined">
      <mat-card-content>
        <div class="metric-label">
          {{label}}
          @if (description) {
            <mat-icon 
              class="info-icon" 
              [matTooltip]="description"
              matTooltipPosition="above">
              info
            </mat-icon>
          }
        </div>
        <div class="metric-value" [ngClass]="getTrendClass()">
          @if (prefix && isValueLoaded) {
            {{prefix}}
          }
          @if (isValueLoaded) {
            @if (!isCurrency) {
              <span [countUp]="value!" [duration]="750"></span>
            } @else {
              <span>{{ value | currency: 'GBP' }}</span>
            }
            @if (suffix) {
              {{suffix}}
            }
          
            @if (trend === 'up') {
              <mat-icon class="trend-icon up">trending_up</mat-icon>
            }
            @if (trend === 'down') {
              <mat-icon class="trend-icon down">trending_down</mat-icon>
            }
          } @else {
            <span class="loading-text">Loading data...</span>
          }
        </div>
        <!-- Sub-values display -->
        @if (subValues && subValues.length > 0) {
          <div class="sub-values-container">
            @for (subItem of subValues; track subItem.label) {
              <div class="sub-value-item">
                <span class="sub-label">{{subItem.label}}: </span>
                @if (isSubValuesLoaded) {
                  <span class="sub-value">
                    <span [countUp]="subItem.value" [duration]="750"></span>
                  </span>
                } @else {
                  <span class="loading-text-small">Loading...</span>
                }
              </div>
            }
          </div>
        }
      </mat-card-content>
    </mat-card>
  `,
  styles: [`
    .metric-card {
      padding: 1rem;
      transition: transform 0.2s;
      height: 100%;
      justify-content: center;
      
      &:hover {
        transform: translateY(-2px);
      }
    }
    .metric-value {
      font-size: 2rem;
      font-weight: bold;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }
    .loading-text {
      color: rgba(0, 0, 0, 0.38);
      font-size: 1.2rem;
      font-style: italic;
    }
    .loading-text-small {
      color: rgba(0, 0, 0, 0.38);
      font-size: 0.9rem;
      font-style: italic;
    }
    .metric-label {
      // color: rgba(0, 0, 0, 0.6);
      text-align: center;
      font-weight: bold;
      margin-bottom: 0.4rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.3rem;
    }
    .info-icon {
      font-size: 16px;
      height: 16px;
      width: 16px;
      color: rgba(0, 0, 0, 0.54);
      cursor: help;
    }
    .trend-icon {
      font-size: 20px;
      height: 20px;
      width: 20px;
      &.up { color: #4caf50; }
      &.down { color: #f44336; }
    }
    .positive { color: #4caf50; }
    .negative { color: #f44336; }
    .neutral { color: var(--mat-sys-primary); }
    .sub-values-container {
      text-align: center;
      margin-top: 0.5rem;
      font-size: 0.9rem;
      display: flex;
      gap: 0.5rem;
      justify-content: center;
      flex-wrap: wrap;
    }
    .sub-value-item {
      /* Styles for individual sub-value items if needed */
    }
    .sub-label {
      font-weight: 500;
    }
    .sub-value {
      font-weight: bold;
      font-size: 1.2rem;
      color: var(--mat-sys-primary);
    }
  `]
})
export class MetricsCardComponent {
  @Input() value?: number;  // Made optional
  @Input() label!: string;
  @Input() description?: string;  // New input for tooltip description
  @Input() prefix?: string;
  @Input() suffix?: string;
  @Input() isCurrency: boolean = false; // New input to indicate if the value is a currency
  @Input() trend?: 'up' | 'down' | 'neutral';
  @Input() subValues?: SubValueItem[]; // New input for multiple sub-values

  get isValueLoaded(): boolean {
    return this.value !== undefined && this.value !== null;
  }

  get isSubValuesLoaded(): boolean {
    // Check if the array exists and is not empty
    return Array.isArray(this.subValues) && this.subValues.length > 0;
  }

  getTrendClass(): string {
    if (!this.trend || this.trend === 'neutral') return 'neutral';
    return this.trend === 'up' ? 'positive' : 'negative';
  }
}